<template>
  <div class="export-wrapper">
    <div v-if="enabledIntegrations.length" class="export-group">
      <p class="export-label">Create with 1-click integration</p>
      <div class="export-buttons">
        <Button
          v-for="integration in enabledIntegrations"
          type="ghost"
          variant="black"
          :key="integration.name"
          :icon="integration.icon"
          data-testid="viewer-ai-workflow-integration"
          @click.prevent="sendTo(integration.name)"
          >{{ generateLabel(integration.name) }}</Button
        >
        <!--        <Button type="link" variant="black" icon="general.plus" data-testid="integration-add" />-->
      </div>
      <HorizontalDivider />
    </div>
    <div class="export-group">
      <p class="export-label">Copy generated content</p>
      <div class="export-buttons">
        <Button type="ghost" variant="black" icon="general.transcription" text="Copy text" data-testid="copy-text" @click.prevent="sendToClipboard" />
        <Button type="ghost" variant="black" icon="general.downloadText" text="Download as PDF" data-testid="download-pdf" @click.prevent="sendToPdf" />
        <Button type="ghost" variant="black" icon="exports.googleDocs" text="Google Docs" data-testid="google-docs" @click.prevent="sendToGoogleDocs" />
        <Button type="ghost" variant="black" icon="exports.googleGmail" text="Gmail" data-testid="gmail" @click.prevent="sendToGmail" />
        <Button type="ghost" variant="black" icon="exports.microsoftWord" text="Microsoft Word" data-testid="ms-word" @click.prevent="sendToWord" />
        <Button type="ghost" variant="black" icon="exports.notion" text="Notion" data-testid="notion" @click.prevent="sendToNotion" />
        <Button
          type="ghost"
          variant="black"
          icon="exports.confluence"
          text="Confluence / Jira"
          data-testid="confluence-jira"
          @click.prevent="sendToConfluenceJira"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, HorizontalDivider } from '@/storybook';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from '@/composables/useToast';
import { useExportUtils } from '@/modules/viewer/composables/useExportUtils';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';
import { useIntegrationStore } from '@/modules/dashboard/integrations/store/useIntegrationStore';

const itemStore = useViewerItemStore();
const { description, displayName } = storeToRefs(itemStore);
const integrationsStore = useIntegrationStore();
const { success } = useToast();
const { copyConfluenceMarkdown, copyMarkdownWithoutImages, copyHtmlFragment, downloadPdf, copyWithVideoLink } = useExportUtils(description, displayName);
const { trackItemOwner } = useViewerTrack();

const enabledIntegrations = computed(() => integrationsStore.integrations.filter(integration => integration.enabled));

const sendTo = (integration: string) => {
  integrationsStore.sendTo(integration);
  success(`Your request has been queued. Please check ${integration} dashboard for updates.`);
};

onMounted(() => {
  integrationsStore.fetchIntegrations();
});

function withTracking(tool: string, fn: () => void) {
  return () => {
    trackItemOwner(TrackEvents.SMARTACTIONS_SEND_TO, { tool: tool });
    fn();
  };
}

function generateLabel(integrationName: string): string {
  switch (integrationName.toLowerCase()) {
    case 'zendesk':
      return 'Zendesk article';
    case 'jira':
      return 'Jira Ticket';
    case 'confluence':
      return 'Confluence page';
    case 'notion':
      return 'Notion page';
    default:
      return integrationName;
  }
}

const sendToClipboard = withTracking('clipboard', copyWithVideoLink);
const sendToPdf = withTracking('pdf', downloadPdf);
const sendToGoogleDocs = withTracking('google_docs', copyHtmlFragment);
const sendToGmail = withTracking('gmail', copyHtmlFragment);
const sendToWord = withTracking('word', copyHtmlFragment);
const sendToNotion = withTracking('notion', copyMarkdownWithoutImages);
const sendToConfluenceJira = withTracking('confluence', copyConfluenceMarkdown);
</script>

<style scoped>
.export-wrapper {
  @apply flex flex-col gap-4;
}
.export-group {
  @apply flex flex-col gap-4;
}
.export-label {
  @apply text-sm font-semibold text-slate-500 dark:text-zinc-300;
}
.export-buttons {
  @apply flex flex-wrap gap-4;
}
</style>
